import { appConfig } from './generated/appConfig';
import { getAccessToken } from './auth';
import type { ImportResult, OcrBackfillCounts, OcrBackfillRun, OcrMode, QueueItem, Reading, ReadingStatus, UserInfo } from './types';

const baseUrl = appConfig.functionUrl.replace(/\/$/, '');

async function authHeaders(): Promise<HeadersInit> {
  // Token-Beschaffung nicht endlos warten lassen: sonst bleibt die Oberfläche beim Laden stehen.
  const token = await Promise.race([
    getAccessToken(),
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Die Anmeldung antwortet nicht. Bitte die App neu öffnen oder abmelden und neu anmelden.')), 30000))
  ]);
  return { Authorization: `Bearer ${token}` };
}

async function throwIfFailed(response: Response): Promise<void> {
  if (response.ok) return;
  // Antwort ohne eigene Fehlermeldung (z. B. 404 vom Server): Status und Adresse nennen, damit die Ursache erkennbar ist.
  const where = (() => { try { return new URL(response.url).pathname; } catch { return ''; } })();
  const payload = await response.json().catch(() => ({ error: `${response.statusText || 'Fehler'} (HTTP ${response.status}${where ? ' bei ' + where : ''})` }));
  throw new Error(payload.error ?? `HTTP ${response.status}`);
}

export async function getMe(): Promise<UserInfo> {
  const response = await fetch(`${baseUrl}/me`, { headers: await authHeaders() });
  await throwIfFailed(response);
  return response.json();
}

export async function fetchReadings(year: number, status?: ReadingStatus): Promise<Reading[]> {
  const query = new URLSearchParams({ year: String(year) });
  if (status) query.set('status', status);
  const response = await fetch(`${baseUrl}/readings?${query}`, { headers: await authHeaders() });
  await throwIfFailed(response);
  return (await response.json()).items;
}

export interface ReadingCounts { total: number; byStatus: Record<string, number> }

/** Anzahl der Ablesungen eines Jahres insgesamt und je Ablesestatus (für die Statusübersicht). */
export async function fetchCounts(year: number): Promise<ReadingCounts> {
  const response = await fetch(`${baseUrl}/readings/counts?year=${year}`, { headers: await authHeaders() });
  await throwIfFailed(response);
  return response.json();
}

export async function submitReading(item: QueueItem): Promise<void> {
  const form = new FormData();
  form.set('readingId', item.readingId);
  form.set('value', String(item.value));
  form.set('previousValue', item.previousValue === undefined ? '' : String(item.previousValue));
  form.set('capturedAt', item.capturedAt);
  form.set('capturedOffline', String(item.capturedOffline));
  form.set('importYear', String(item.importYear));
  form.set('importId', item.importId ?? '');
  form.set('meterNumber', item.meterNumber);
  form.set('street', item.street);
  if (item.photo) form.set('photo', item.photo, `${item.importYear}_${item.meterNumber}.jpg`);
  const response = await fetch(`${baseUrl}/readings/${encodeURIComponent(item.readingId)}/submit`, {
    method: 'POST', headers: await authHeaders(), body: form
  });
  await throwIfFailed(response);
}

/** Ableseliste (Excel .xlsx oder CSV) an die Function senden. Mit `dryRun` nur Vorschau: es wird nichts angelegt. */
export async function importFile(file: File, dryRun: boolean, defaultYear?: number): Promise<ImportResult> {
  const query = [dryRun ? 'dryRun=1' : '', defaultYear ? `year=${defaultYear}` : ''].filter(Boolean).join('&');
  const response = await fetch(`${baseUrl}/verwaltung/import${query ? '?' + query : ''}`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': file.type || 'application/octet-stream' },
    body: file
  });
  await throwIfFailed(response);
  return response.json();
}

/** Export eines Ablesejahres. `columns`: `versorger`, `vorlage`, `alle` oder interne Spaltennamen mit Komma; `status` begrenzt auf einen Ablesestatus. */
export async function downloadExport(year: number, format: 'xlsx' | 'csv', options: { columns?: string; status?: string } = {}): Promise<void> {
  const query = new URLSearchParams({ year: String(year), format });
  if (options.columns) query.set('columns', options.columns);
  if (options.status) query.set('status', options.status);
  const response = await fetch(`${baseUrl}/verwaltung/export?${query}`, { headers: await authHeaders() });
  await throwIfFailed(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `PV-Ablesungen_${year}${options.status ? `_${options.status.replaceAll(' ', '-')}` : ''}.${format}`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function markChecked(id: string): Promise<void> {
  const response = await fetch(`${baseUrl}/verwaltung/readings/${encodeURIComponent(id)}/check`, {
    method: 'POST', headers: { ...(await authHeaders()), 'Content-Type': 'application/json' }, body: '{}'
  });
  await throwIfFailed(response);
}

export interface SettingsInfo {
  current: { sharePointSiteUrl?: string; sharePointList: string; sharePointPhotoLibrary: string; ocrMode?: OcrMode; updatedBy?: string; updatedAt?: string };
  defaults: { sharePointSiteUrl?: string; sharePointList: string; sharePointPhotoLibrary: string; ocrMode?: OcrMode };
  /** Meldung, wenn die App auf die gewählte Website nicht zugreifen darf (dann sind die Listen leer). */
  siteError?: string;
  /** Ob der OCR-Dienst (Azure AI Vision) in der Function App eingerichtet ist. */
  ocr?: { configured: boolean };
  siteUrl: string;
  /** Nur Listen und Bibliotheken, die der Struktur der App entsprechen. */
  lists: string[];
  libraries: string[];
  /** Anzahl der übrigen, nicht passenden Listen und Bibliotheken der Website (werden nicht angezeigt). */
  hidden?: { lists: number; libraries: number };
}

/** `site`: Adresse einer anderen SharePoint-Website; die Listen und Bibliotheken dieser Website werden geliefert. */
export async function getSettings(site?: string): Promise<SettingsInfo> {
  const response = await fetch(`${baseUrl}/settings${site ? `?site=${encodeURIComponent(site)}` : ''}`, { headers: await authHeaders() });
  await throwIfFailed(response);
  return response.json();
}

export async function saveSettings(sharePointList: string, sharePointPhotoLibrary: string, ocrMode: OcrMode, sharePointSiteUrl?: string): Promise<void> {
  const response = await fetch(`${baseUrl}/settings`, {
    method: 'PUT', headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ sharePointList, sharePointPhotoLibrary, ocrMode, sharePointSiteUrl })
  });
  await throwIfFailed(response);
}

export interface StructureResult { ok: boolean; list: { created: boolean; added: string[] }; library: { created: boolean; added: string[] } }

/** Legt Liste und Dokumentbibliothek mit der Struktur der App an bzw. ergänzt fehlende Spalten einer vorhandenen. */
/** Ein Administrator muss der App zustimmen, damit sie im Namen des Benutzers Listen anlegen darf. */
export class ConsentRequiredError extends Error {}

export async function createStructure(sharePointList: string, sharePointPhotoLibrary: string, sharePointSiteUrl?: string): Promise<StructureResult> {
  const response = await fetch(`${baseUrl}/settings/structure`, {
    method: 'POST', headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ sharePointList, sharePointPhotoLibrary, sharePointSiteUrl })
  });
  if (response.status === 403) {
    const payload = await response.clone().json().catch(() => undefined) as { code?: string; error?: string } | undefined;
    if (payload?.code === 'consent_required') throw new ConsentRequiredError(payload.error ?? 'Zustimmung eines Administrators erforderlich.');
  }
  await throwIfFailed(response);
  return response.json();
}

export interface MeterOcrResult { rawText: string; meterReading?: number; confidence?: number }

/** Liest den Zählerstand vom Foto (nur wenn die Zählerstand-Erkennung für den Nutzer gilt). */
export async function readMeterOcr(photo: Blob): Promise<MeterOcrResult> {
  const response = await fetch(`${baseUrl}/ocr`, {
    method: 'POST', headers: { ...(await authHeaders()), 'Content-Type': photo.type || 'image/jpeg' }, body: photo
  });
  await throwIfFailed(response);
  return response.json();
}

/** Stand der nachträglichen Auswertung der Fotos eines Jahres. */
export async function ocrBackfillStatus(year: number): Promise<OcrBackfillCounts> {
  const response = await fetch(`${baseUrl}/verwaltung/ocr/status?year=${year}`, { headers: await authHeaders() });
  await throwIfFailed(response);
  return response.json();
}

/** Ein Schritt der nachträglichen Auswertung: `missing` liest Fotos ohne OCR-Werte, `recompute` berechnet den Wert aus dem gespeicherten Rohtext neu. */
export async function ocrBackfillRun(year: number, mode: 'missing' | 'recompute', skip: number): Promise<OcrBackfillRun> {
  const response = await fetch(`${baseUrl}/verwaltung/ocr/run?year=${year}&mode=${mode}&skip=${skip}`, { method: 'POST', headers: await authHeaders() });
  await throwIfFailed(response);
  return response.json();
}

export async function geocodeAddresses(addresses: Array<{ street: string; city: string }>): Promise<Array<{ lat: number; lon: number } | null>> {
  const response = await fetch(`${baseUrl}/geocode`, {
    method: 'POST', headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ addresses })
  });
  await throwIfFailed(response);
  return (await response.json()).results;
}

/* ---- Erkennung verbessern: Testfälle (Zählerfoto + richtiger Zählerstand) ---- */

export interface OcrTestItem {
  id: string; label: string; expected?: number; recognized?: number; recognizedAtCapture?: number; confidence?: number;
  rawText: string; hasText: boolean; match: boolean; createdBy?: string; createdAt?: string;
}
export interface OcrTestSummary { total: number; matched: number; differing: number; noText: number }
export type OcrTestsInfo =
  | { configured: false; defaults: { list: string; library: string; siteUrl: string }; ocrConfigured: boolean }
  | { configured: true; list: string; library: string; siteUrl: string; ocrConfigured: boolean; items: OcrTestItem[]; summary: OcrTestSummary };

/** Testfälle mit Zusammenfassung; der erkannte Wert wird dabei mit der aktuellen Auswertung aus dem gespeicherten Rohtext neu berechnet. */
export async function fetchOcrTests(): Promise<OcrTestsInfo> {
  const response = await fetch(`${baseUrl}/verwaltung/ocr/tests`, { headers: await authHeaders() });
  await throwIfFailed(response);
  return response.json();
}

/** Legt einen Testfall an: Die Erkennung läuft einmal, Foto, richtiger Zählerstand (Dezimalstellen mit Komma) und Rohtext werden gespeichert. */
export async function createOcrTest(photo: Blob, expected: string, label: string): Promise<OcrTestItem> {
  const query = new URLSearchParams({ expected, label });
  const response = await fetch(`${baseUrl}/verwaltung/ocr/tests?${query}`, {
    method: 'POST', headers: { ...(await authHeaders()), 'Content-Type': photo.type || 'image/jpeg' }, body: photo
  });
  await throwIfFailed(response);
  return response.json();
}

export async function deleteOcrTest(id: string): Promise<void> {
  const response = await fetch(`${baseUrl}/verwaltung/ocr/tests/${encodeURIComponent(id)}`, { method: 'DELETE', headers: await authHeaders() });
  await throwIfFailed(response);
}

/** Gespeichertes Zählerfoto einer Ablesung als Adresse für `<img>` (muss mit `URL.revokeObjectURL` freigegeben werden). */
export async function fetchReadingPhoto(id: string): Promise<string> {
  const response = await fetch(`${baseUrl}/readings/${encodeURIComponent(id)}/photo`, { headers: await authHeaders() });
  await throwIfFailed(response);
  return URL.createObjectURL(await response.blob());
}

/** Testfoto als Adresse für `<img>` (muss mit `URL.revokeObjectURL` freigegeben werden). */
export async function fetchOcrTestPhoto(id: string): Promise<string> {
  const response = await fetch(`${baseUrl}/verwaltung/ocr/tests/${encodeURIComponent(id)}/photo`, { headers: await authHeaders() });
  await throwIfFailed(response);
  return URL.createObjectURL(await response.blob());
}

/** Legt Liste und Bibliothek der Testfälle an (im Namen des Benutzers) und speichert die Namen in den Einstellungen. */
export async function setupOcrTests(list: string, library: string, site?: string): Promise<void> {
  const response = await fetch(`${baseUrl}/verwaltung/ocr-testsetup`, {
    method: 'POST', headers: { ...(await authHeaders()), 'Content-Type': 'application/json' }, body: JSON.stringify({ list, library, site })
  });
  if (response.status === 403) {
    const payload = await response.clone().json().catch(() => undefined) as { code?: string; error?: string } | undefined;
    if (payload?.code === 'consent_required') throw new ConsentRequiredError(payload.error ?? 'Zustimmung eines Administrators erforderlich.');
  }
  await throwIfFailed(response);
}
